import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import { NodeKind, systemLegend, systemStages } from '../data/systemFlow';
const COLUMN_W = 290;
const NODE_W = 224;
const NODE_H = 58;
const V_PITCH = 84;
const PAD = 72;
const GAP = COLUMN_W - NODE_W;
const FOCUS_W = 620;
interface KindStyle {
  bar: string;
  panel: string;
  text: string;
  swatch: string;
}
const kindStyles: Record<NodeKind, KindStyle> = {
  hire: {
    bar: 'bg-[#4C7BD9]',
    panel: 'bg-[#101B31]/90 border-[#25375C]',
    text: 'text-slate-200',
    swatch: 'bg-[#4C7BD9]'
  },
  partner: {
    bar: 'bg-[#7FE0C3]',
    panel: 'bg-[#0C2A24]/90 border-[#7FE0C3]/40',
    text: 'text-[#B9F2E2]',
    swatch: 'bg-[#7FE0C3]'
  },
  coaching: {
    bar: 'bg-amber-400',
    panel: 'bg-[#2A1F06]/90 border-amber-400/40',
    text: 'text-amber-200',
    swatch: 'bg-amber-400'
  },
  auditing: {
    bar: 'bg-slate-300',
    panel: 'bg-[#171C26]/90 border-slate-400/40',
    text: 'text-slate-300',
    swatch: 'bg-slate-300'
  }
};
interface PlacedNode {
  id: string;
  stage: number;
  label: string;
  kind: NodeKind;
  x: number;
  y: number;
  w: number;
}
interface Edge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
export function SystemDiagram() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.4);
  const [fitScale, setFitScale] = useState(0.4);
  const [offset, setOffset] = useState({
    x: 0,
    y: 0
  });
  const [ready, setReady] = useState(false);
  const [focused, setFocused] = useState<number | null>(null);
  const viewRef = useRef({
    scale: 0.4,
    offset: {
      x: 0,
      y: 0
    }
  });
  viewRef.current = {
    scale,
    offset
  };
  const {
    spineY,
    height,
    width
  } = useMemo(() => {
    const maxAbove = Math.max(...systemStages.map(s => s.above.length));
    const maxBelow = Math.max(...systemStages.map(s => s.below.length));
    const spine = PAD + maxAbove * V_PITCH;
    return {
      spineY: spine,
      height: spine + maxBelow * V_PITCH + PAD + NODE_H,
      width: PAD * 2 + systemStages.length * NODE_W + (systemStages.length - 1) * GAP
    };
  }, []);

  // Column widths redistribute on hover: the focused stage grows and the
  // others compress by the same total amount, so the board never reflows.
  const {
    nodes,
    stageChips,
    hitZones,
    branchEdges,
    junctions,
    spineStart,
    spineEnd
  } = useMemo(() => {
    const count = systemStages.length;
    const widths = systemStages.map(() => NODE_W);
    if (focused !== null) {
      const shrink = (FOCUS_W - NODE_W) / (count - 1);
      widths.forEach((_, i) => {
        widths[i] = i === focused ? FOCUS_W : NODE_W - shrink;
      });
    }
    const nodes: PlacedNode[] = [];
    const stageChips: Array<{
      index: number;
      label: string;
      owner: string;
      x: number;
      w: number;
    }> = [];
    const branchEdges: Edge[] = [];
    const junctions: Array<{
      x: number;
      y: number;
    }> = [];
    let cursor = PAD;
    systemStages.forEach((stage, index) => {
      const x = cursor;
      const w = widths[index];
      const cx = x + w / 2;
      cursor += w + GAP;
      stageChips.push({
        index,
        label: stage.label,
        owner: stage.owner,
        x,
        w
      });
      stage.above.forEach((node, j) => {
        const y = spineY - (j + 1) * V_PITCH;
        nodes.push({
          id: `${index}-a-${j}`,
          stage: index,
          ...node,
          x,
          y,
          w
        });
        branchEdges.push({
          x1: cx,
          y1: y + NODE_H,
          x2: cx,
          y2: j === 0 ? spineY : y + V_PITCH
        });
      });
      if (stage.above.length) junctions.push({
        x: cx,
        y: spineY
      });
      stage.below.forEach((node, j) => {
        const y = spineY + (j + 1) * V_PITCH;
        nodes.push({
          id: `${index}-b-${j}`,
          stage: index,
          ...node,
          x,
          y,
          w
        });
        branchEdges.push({
          x1: cx,
          y1: j === 0 ? spineY + NODE_H : y - V_PITCH + NODE_H,
          x2: cx,
          y2: y
        });
      });
      if (stage.below.length) junctions.push({
        x: cx,
        y: spineY + NODE_H
      });
    });

    // One invisible hit zone per stage, spanning that column's full vertical
    // content plus the gutters, so moving between its containers never drops
    // focus and moving sideways hands focus straight to the next stage.
    const hitZones = stageChips.map(chip => {
      const stage = systemStages[chip.index];
      const top = spineY - stage.above.length * V_PITCH - V_PITCH / 2;
      const bottom = spineY + NODE_H + stage.below.length * V_PITCH + V_PITCH / 2;
      return {
        index: chip.index,
        x: chip.x - GAP / 2,
        y: Math.max(0, top),
        w: chip.w + GAP,
        h: Math.min(height, bottom) - Math.max(0, top)
      };
    });
    return {
      nodes,
      stageChips,
      hitZones,
      branchEdges,
      junctions,
      spineStart: PAD,
      spineEnd: cursor - GAP
    };
  }, [focused, spineY, height]);

  // Fit the whole board in view on mount and whenever the container resizes.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const fit = () => {
      const {
        clientWidth,
        clientHeight
      } = element;
      if (!clientWidth || !clientHeight) return;
      const next = Math.min(clientWidth / width, clientHeight / height);
      setFitScale(next);
      setScale(next);
      setOffset({
        x: (clientWidth - width * next) / 2,
        y: (clientHeight - height * next) / 2
      });
      setReady(true);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [width, height]);

  // Ctrl/Cmd + wheel zooms toward the cursor.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      const pointerY = event.clientY - rect.top;
      const current = viewRef.current;
      const factor = Math.exp(-event.deltaY * 0.002);
      const next = Math.min(2, Math.max(0.15, current.scale * factor));
      const ratio = next / current.scale;
      setScale(next);
      setOffset({
        x: pointerX - (pointerX - current.offset.x) * ratio,
        y: pointerY - (pointerY - current.offset.y) * ratio
      });
    };
    element.addEventListener('wheel', handleWheel, {
      passive: false
    });
    return () => element.removeEventListener('wheel', handleWheel);
  }, []);
  const zoomIn = () => setScale(s => Math.min(Number((s + 0.1).toFixed(2)), 2));
  const zoomOut = () => setScale(s => Math.max(Number((s - 0.05).toFixed(2)), 0.15));
  const reset = () => {
    setScale(fitScale);
    const element = containerRef.current;
    if (element) {
      setOffset({
        x: (element.clientWidth - width * fitScale) / 2,
        y: (element.clientHeight - height * fitScale) / 2
      });
    }
  };
  const centerY = spineY + NODE_H / 2;
  return <div ref={containerRef} className="w-full h-full relative overflow-hidden bg-[#070B14]" data-id="element-587" data-mp="3f37wo1yg3zbz">
      {/* Blueprint grid */}
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.35]" style={{
      backgroundImage: 'linear-gradient(to right, rgba(127,224,195,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(127,224,195,0.06) 1px, transparent 1px)',
      backgroundSize: '48px 48px'
    }} data-id="element-588" data-mp="3k2uaaci6rhlb" />

      {/* Legend */}
      <div className="absolute top-4 right-4 z-20 rounded-lg border border-[#1B2942] bg-[#0A1120]/90 backdrop-blur-sm px-6 py-5" data-id="element-589" data-mp="6upt2slaigan">
        <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#7FE0C3] mb-4" data-id="element-590" data-mp="ujitp2cuxcbl">
          Who Owns What
        </h3>
        <ul className="space-y-3" data-id="element-591" data-mp="zdn11mq1bmd7">
          {systemLegend.map(item => <li key={item.label} className="flex items-center" data-id="element-592" data-mp="2vg9lpbsuw612">
              <span className={`w-1.5 h-6 rounded-sm mr-4 ${kindStyles[item.kind].swatch}`} aria-hidden="true" data-id="element-593" data-mp="2qnyju3ek2a5v" />
              <span className="text-lg text-slate-200" data-id="element-594" data-mp="2lmspk158yeu8">{item.label}</span>
            </li>)}
        </ul>
      </div>

      {/* Zoom controls */}
      <div className="absolute bottom-4 right-4 z-20 flex items-center gap-1 rounded-lg border border-[#1B2942] bg-[#0A1120]/90 backdrop-blur-sm p-1" data-id="element-595" data-mp="3echuuqozp11n">
        <button type="button" onClick={zoomOut} aria-label="Zoom out" className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-[#152238] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-596" data-mp="34fmvk5kw6wmb">
          <Minus size={16} data-id="element-597" data-mp="1oz4m8p9ugquq" />
        </button>
        <span className="w-12 text-center text-xs font-medium text-slate-400 tabular-nums" data-id="element-598" data-mp="1oy36i7hvvg6o">
          {Math.round(scale * 100)}%
        </span>
        <button type="button" onClick={zoomIn} aria-label="Zoom in" className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-[#152238] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-599" data-mp="8ifd8a9i10zj">
          <Plus size={16} data-id="element-600" data-mp="n7lkmlihpxii" />
        </button>
        <button type="button" onClick={reset} aria-label="Reset zoom" className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-[#152238] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-601" data-mp="n6jxhio79kkf">
          <RotateCcw size={16} data-id="element-602" data-mp="116kycy4gsrzw" />
        </button>
      </div>

      <p className="absolute bottom-5 left-4 z-20 text-[10px] uppercase tracking-[0.2em] text-slate-600" data-id="element-603" data-mp="2xzwgar5vygf7">
        Hover a stage to expand · Drag to pan · Ctrl + scroll to zoom
      </p>

      {/* Canvas */}
      <motion.div drag dragMomentum={false} className={`w-full h-full cursor-grab active:cursor-grabbing transition-opacity duration-200 ease-out ${ready ? 'opacity-100' : 'opacity-0'}`} data-id="element-604" data-mp="2kzf943t85y59">
        <div className="origin-top-left relative" style={{
        width,
        height,
        transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`
      }} onMouseLeave={() => setFocused(null)}
      // Moving onto empty canvas (not a stage or branch) returns to the
      // normal view, so hovering just below a column releases the focus.
      onMouseOver={event => {
        if (event.target === event.currentTarget) {
          setFocused(null);
        }
      }} data-id="element-605" data-mp="2uw0eiq95pb9m">
          <svg className="absolute inset-0 pointer-events-none" width={width} height={height} aria-hidden="true" data-id="element-606" data-mp="3nma0toamzn6k">
            {/* Branch lines */}
            {branchEdges.map((edge, index) => <line key={index} x1={edge.x1} y1={edge.y1} x2={edge.x2} y2={edge.y2} stroke="#7FE0C3" strokeWidth={1.5} strokeOpacity={0.22} data-id="element-607" data-mp="1fxdzb8gjvvqh" data-mpi={index} />)}

            {/* Junction dots */}
            {junctions.map((point, index) => <circle key={index} cx={point.x} cy={point.y} r={3} fill="#7FE0C3" fillOpacity={0.5} data-id="element-608" data-mp="1zmwos5h4h5vk" data-mpi={index} />)}

            {/* Spine */}
            <line x1={spineStart} y1={centerY} x2={spineEnd} y2={centerY} stroke="#7FE0C3" strokeWidth={2} strokeOpacity={0.2} data-id="element-609" data-mp="1kzbmgds2naop" />
            <line x1={spineStart} y1={centerY} x2={spineEnd} y2={centerY} stroke="#7FE0C3" strokeWidth={2} strokeOpacity={0.8} strokeDasharray="10 22" className="motion-safe:animate-[flow_1.6s_linear_infinite]" data-id="element-610" data-mp="2dlwd1c0q3ir7" />
          </svg>

          {/* Invisible per-stage hover zones */}
          {hitZones.map(zone => <div key={`zone-${zone.index}`} aria-hidden="true" onMouseEnter={() => setFocused(zone.index)} className="absolute transition-[left,width] duration-200 ease-out" style={{
          left: zone.x,
          top: zone.y,
          width: zone.w,
          height: zone.h,
          zIndex: 0
        }} data-id="element-611" data-mp="3o6u6w4m1xme3" />)}

          {/* Stage chips */}
          {stageChips.map(chip => {
          const accent = chip.owner === 'partner' ? '#7FE0C3' : chip.owner === 'retention' ? '#FBBF24' : '#4C7BD9';
          const active = focused === chip.index;
          const dimmed = focused !== null && !active;
          return <div key={chip.label} onMouseEnter={() => setFocused(chip.index)} className="absolute rounded-md border bg-[#0B1424]/95 backdrop-blur-sm flex flex-col items-center justify-center transition-[left,width,opacity] duration-200 ease-out" style={{
            left: chip.x,
            top: spineY,
            width: chip.w,
            height: NODE_H,
            borderColor: `${accent}66`,
            boxShadow: active ? `0 0 0 1px ${accent}66, 0 0 60px ${accent}55` : `0 0 0 1px ${accent}1A, 0 0 24px ${accent}22`,
            opacity: dimmed ? 0.28 : 1,
            filter: dimmed ? 'saturate(0.5)' : 'none',
            zIndex: active ? 5 : 1
          }} data-id="element-612" data-mp="21c77royohvo4">
                <span className="absolute top-0 left-0 rounded-t-md transition-[height] duration-200 ease-out" style={{
              height: active ? 4 : 2,
              width: '100%',
              backgroundColor: accent
            }} aria-hidden="true" data-id="element-613" data-mp="s9makhr8g40e" />
                <span className="absolute -top-5 left-0 text-[10px] font-bold tabular-nums tracking-[0.2em]" style={{
              color: `${accent}99`
            }} data-id="element-614" data-mp="4kjmar23fcwx">
                  {String(chip.index + 1).padStart(2, '0')}
                </span>
                <span className="font-bold uppercase tracking-[0.1em] text-white text-center px-3 leading-tight transition-[font-size] duration-200 ease-out" style={{
              fontSize: active ? 34 : 16
            }} data-id="element-615" data-mp="dj4ze6ospzqb">
                  {chip.label}
                </span>
              </div>;
        })}

          {/* Branch nodes */}
          {nodes.map(node => {
          const style = kindStyles[node.kind];
          const active = focused === node.stage;
          const dimmed = focused !== null && !active;
          return <div key={node.id} onMouseEnter={() => setFocused(node.stage)} className={`absolute rounded-md border overflow-hidden flex items-center backdrop-blur-sm transition-[left,width,opacity] duration-200 ease-out ${style.panel}`} style={{
            left: node.x,
            top: node.y,
            width: node.w,
            height: NODE_H,
            opacity: dimmed ? 0.25 : 1,
            filter: dimmed ? 'saturate(0.4)' : 'none',
            zIndex: active ? 5 : 1
          }} data-id="element-616" data-mp="34hj84ko0woi7">
                <span className={`h-full shrink-0 transition-[width] duration-200 ease-out ${style.bar}`} style={{
              width: active ? 6 : 3
            }} aria-hidden="true" data-id="element-617" data-mp="27t3f2elpfx0c" />
                <span className={`px-4 leading-tight tracking-wide transition-[font-size] duration-200 ease-out ${style.text}`} style={{
              fontSize: active ? 28 : 15
            }} data-id="element-618" data-mp="227b9774ct3lq">
                  {node.label}
                </span>
              </div>;
        })}
        </div>
      </motion.div>
    </div>;
}